import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, PaperPlaneTilt } from '@phosphor-icons/react';
import { API_BASE } from '../config/api';

export function UploadWindow({ isOpen, onClose, onAnalysisComplete, token }) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAbsorbing, setIsAbsorbing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragWindow, setIsDragWindow] = useState(false);
  const dragStartRef = useRef(null);
  const fileInputRef = useRef(null);

  const API = API_BASE;

  // Window dragging
  const handleWindowDragStart = (e) => {
    if (e.target.closest('input, button, [data-no-drag]')) return;
    setIsDragWindow(true);
    dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  const handleWindowDrag = useCallback((e) => {
    if (isDragWindow && dragStartRef.current) {
      setPosition({ x: e.clientX - dragStartRef.current.x, y: e.clientY - dragStartRef.current.y });
    }
  }, [isDragWindow]);

  const handleWindowDragEnd = () => setIsDragWindow(false);

  // File drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) setFiles(prev => [...prev, ...dropped]);
  }, []);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) setFiles(prev => [...prev, file]);
      }
    }
  }, []);

  const handleSend = async () => {
    if (!files.length && !message.trim()) return;
    setIsAnalyzing(true);

    try {
      if (files.length > 0) {
        // Send first file for analysis
        const formData = new FormData();
        formData.append('file', files[0]);
        if (message.trim()) formData.append('context', message.trim());

        const res = await fetch(`${API}/chat/analyze`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: formData,
        });

        const data = await res.json();
        
        if (res.ok) {
          // Absorption animation
          setIsAbsorbing(true);
          setTimeout(() => {
            onAnalysisComplete(data.response, files[0].name);
            setFiles([]);
            setMessage('');
            setIsAbsorbing(false);
            setIsAnalyzing(false);
            onClose();
          }, 1000);
          return;
        } else {
          onAnalysisComplete('Esse arquivo nao chegou direito ate mim. Tenta enviar de novo.', files[0]?.name || 'arquivo');
        }
      }
    } catch {
      onAnalysisComplete('Tive dificuldade em processar isso. Tenta de novo?', 'arquivo');
    }

    setFiles([]);
    setMessage('');
    setIsAnalyzing(false);
    onClose();
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const getPreview = (file) => {
    if (file.type.startsWith('image/')) return URL.createObjectURL(file);
    return null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose}
            onMouseMove={handleWindowDrag} onMouseUp={handleWindowDragEnd} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={isAbsorbing
              ? { opacity: 0, scaleX: 0, scaleY: 0.02, transition: { duration: 1, ease: "easeInOut" } }
              : { opacity: 1, scale: 1, y: 0 }
            }
            exit={{ opacity: 0, scale: 0.9 }}
            style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] max-w-[90vw] z-50"
            onMouseDown={handleWindowDragStart}
            onMouseMove={handleWindowDrag}
            onMouseUp={handleWindowDragEnd}
            onPaste={handlePaste}
            data-testid="upload-window"
          >
            {isAbsorbing && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.8, 0], scale: [1, 0.3, 0] }}
                transition={{ duration: 1 }}
                className="absolute inset-0 bg-blue-500/30 rounded-2xl blur-xl pointer-events-none" />
            )}

            <div className="bg-[#08081a]/95 backdrop-blur-xl border border-blue-500/10 rounded-2xl overflow-hidden shadow-2xl shadow-blue-900/20">
              {/* Header - draggable */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 cursor-grab active:cursor-grabbing">
                <span className="text-xs font-mono text-blue-400/60 tracking-widest uppercase">
                  {isAnalyzing ? 'Analisando...' : 'Enviar para Athena'}
                </span>
                <button onClick={onClose} className="p-1 hover:bg-white/5 rounded" data-testid="close-upload" data-no-drag>
                  <X size={14} className="text-white/30" />
                </button>
              </div>

              {/* Analysis progress */}
              {isAnalyzing && !isAbsorbing && (
                <div className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full" />
                    <span className="text-xs text-blue-400/60">Athena esta processando...</span>
                  </div>
                </div>
              )}

              {/* Drop zone */}
              {!isAnalyzing && (
                <>
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onClick={() => fileInputRef.current?.click()}
                    className={`mx-4 mt-4 p-5 border border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                      isDragging ? 'border-blue-400/50 bg-blue-500/5' : 'border-white/8 hover:border-white/15'
                    }`}
                    data-testid="drop-zone" data-no-drag
                  >
                    <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.txt,.doc,.docx"
                      className="hidden" onChange={(e) => setFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                    <div className="text-center">
                      <Upload size={22} className={`mx-auto mb-2 ${isDragging ? 'text-blue-400' : 'text-white/20'}`} />
                      <p className="text-xs text-white/30">Arraste, cole ou clique</p>
                      <p className="text-[10px] text-white/15 mt-1">Imagens, videos, documentos</p>
                    </div>
                  </div>

                  {/* File previews */}
                  {files.length > 0 && (
                    <div className="mx-4 mt-3 space-y-2 max-h-40 overflow-y-auto" data-no-drag>
                      {files.map((f, i) => {
                        const preview = getPreview(f);
                        return (
                          <div key={i} className="flex items-center gap-3 px-3 py-2 bg-white/3 rounded-lg">
                            {preview && <img src={preview} alt="" className="w-10 h-10 object-cover rounded" />}
                            <span className="text-xs text-white/50 truncate flex-1">{f.name}</span>
                            <span className="text-[10px] text-white/20">{(f.size / 1024).toFixed(0)}KB</span>
                            <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-white/20 hover:text-white/50">
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Context */}
                  <div className="mx-4 mt-3" data-no-drag>
                    <input type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                      placeholder="O que voce quer que eu analise?"
                      className="w-full px-3 py-2 bg-white/3 border border-white/5 rounded-lg text-xs text-white/80 placeholder-white/20 outline-none focus:border-blue-500/20"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                    />
                  </div>

                  {/* Send */}
                  <div className="p-4" data-no-drag>
                    <button onClick={handleSend} disabled={!files.length}
                      className="w-full py-2.5 bg-blue-500/10 border border-blue-500/15 rounded-xl text-xs text-blue-400/80 font-mono tracking-wider uppercase hover:bg-blue-500/15 transition-colors disabled:opacity-20 flex items-center justify-center gap-2"
                      data-testid="send-to-athena">
                      <PaperPlaneTilt size={14} />
                      Enviar para Athena
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
